import { Button } from "@/components/ui/button";
import { Facebook, Linkedin, Mail, Link as LinkIcon, Twitter, Instagram, Share2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Event } from "@/data/events";
import { useIsMobile } from "@/hooks/use-mobile";

interface EventShareProps {
  event: Event;
}

const SHARE_BASE = "https://share.rcnmo.com";
const FB_SHARE_BASE = "https://vkhjcsfgudgocgmvyigy.supabase.co/functions/v1/event-share";

const EventShare = ({ event }: EventShareProps) => {
  const isMobile = useIsMobile();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://rcnmo.com";
  const pathChapter =
    typeof window !== "undefined" && window.location.pathname.startsWith("/union")
      ? "union"
      : "washington";
  const dateStr = format(event.date, "EEEE, MMMM d, yyyy");
  const occurrenceDate = `${event.date.getFullYear()}-${String(event.date.getMonth() + 1).padStart(2, "0")}-${String(event.date.getDate()).padStart(2, "0")}`;
  // Stable cache-busting version. Bump this when the share renderer changes
  // so Facebook re-scrapes instead of using a poisoned cached preview.
  const SHARE_VERSION = "20260513b";
  const inAppUrl = event.dbEventId
    ? `${origin}/${pathChapter}/events?event=${event.dbEventId}&date=${occurrenceDate}`
    : `${origin}/${pathChapter}/events`;
  const url = event.dbEventId
    ? `${SHARE_BASE}/event-share?id=${event.dbEventId}&date=${occurrenceDate}&chapter=${pathChapter}&v=${SHARE_VERSION}`
    : inAppUrl;
  const fbUrl = event.dbEventId
    ? `${FB_SHARE_BASE}?id=${event.dbEventId}&date=${occurrenceDate}&chapter=${pathChapter}&v=${SHARE_VERSION}`
    : inAppUrl;
  const title = `${event.title} — ${dateStr}`;
  const body = `${event.title}\n${dateStr} • ${event.startTime} - ${event.endTime}\n${event.location}${event.locationAddress ? `, ${event.locationAddress}` : ""}\n\n${event.description}`;

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedBody = encodeURIComponent(body);

  // Use m.facebook.com on mobile — its mobile site handles app handoff more reliably.
  const fbBase = isMobile ? "https://m.facebook.com/sharer.php" : "https://www.facebook.com/sharer/sharer.php";

  const links = [
    {
      name: "Facebook",
      icon: Facebook,
      href: `${fbBase}?u=${encodeURIComponent(fbUrl)}`,
    },
    {
      name: "X",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=${encodedBody}%0A%0A${encodedUrl}`,
    },
  ];

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const canNativeShare =
    typeof navigator !== "undefined" && typeof (navigator as Navigator).share === "function";

  const handleNativeShare = async () => {
    try {
      await (navigator as Navigator).share({ title, text: body, url });
    } catch (err) {
      // User cancelled or share failed — fall back to copying the link.
      if ((err as DOMException)?.name !== "AbortError") {
        copy(url, "Link copied to clipboard");
      }
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground mr-1">Share:</span>

      {isMobile ? (
        <>
          <Button
            variant="default"
            size="sm"
            onClick={handleNativeShare}
            aria-label="Share via your device"
            className="gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Copy link"
            onClick={() => copy(url, "Link copied to clipboard")}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          {canNativeShare && (
            <Button
              variant="default"
              size="sm"
              onClick={handleNativeShare}
              aria-label="Share via your device"
              className="gap-2"
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          )}

          {links.map((l) => (
            <Button
              key={l.name}
              variant="outline"
              size="icon"
              asChild
              aria-label={`Share on ${l.name}`}
            >
              <a href={l.href} target="_blank" rel="noopener noreferrer">
                <l.icon className="h-4 w-4" />
              </a>
            </Button>
          ))}
          <Button
            variant="outline"
            size="icon"
            aria-label="Share on Instagram"
            onClick={() => copy(`${title}\n${url}`, "Link copied — paste into your Instagram story or DM")}
          >
            <Instagram className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="Copy link"
            onClick={() => copy(url, "Link copied to clipboard")}
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
};

export default EventShare;
